import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { ApiMeta } from '@/lib/http-core';

// #2 정산 상품별 매출 집계 — be-core 37ee06d 계약.
// GET /api/admin/v1/orders/sales-aggregation (order-service, /api prefix · dev proxy 매핑됨).
// 생성 OpenAPI 타입에 아직 미반영 → adminFetch + 수동 envelope/meta 파싱.
// paid(CAPTURED)만 집계. RBAC=SUPER_ADMIN/OPS_ADMIN/AUDITOR(BE 강제).

export type SalesAggregationSort = 'salesAmountKrw' | 'quantity';

/** QA-588 검색어 종류(부분 일치) — BO-TO-BE-2026-10-27-sales-aggregation-qa588. */
export type SalesAggregationSearchType = 'PRODUCT_CODE' | 'SELF_PRODUCT_CODE' | 'PRODUCT_NAME';
/** QA-588 집계 기준 — 상품코드(기본) / 자체상품코드. */
export type SalesAggregationGroupBy = 'PRODUCT_CODE' | 'SELF_PRODUCT_CODE';
/** 기간 기준 — BE meta.dateBasis. 없으면 주문일(BE 는 주문 생성 시각으로 거른다). */
export type SalesAggregationDateBasis = 'ORDERED_AT' | 'PAID_AT';

export interface SalesAggregationParams {
  /** 집계 시작일 (YYYY-MM-DD). */
  from?: string;
  /** 집계 종료일 (YYYY-MM-DD). */
  to?: string;
  /** 특정 상품코드 필터(부분/정확은 BE 정의). */
  productCode?: string;
  /** QA-588 검색어 종류 + 검색어(부분 일치). */
  searchType?: SalesAggregationSearchType;
  keyword?: string;
  /** QA-588 집계 기준. */
  groupBy?: SalesAggregationGroupBy;
  /** QA-588 기간 기준(order #125) — 없으면 BE 기본 주문일. */
  dateBasis?: SalesAggregationDateBasis;
  sort?: SalesAggregationSort;
  /** 1-based 페이지(서버는 0-based). */
  page?: number;
  size?: number;
}

export interface SalesAggregationRow {
  productCode: string;
  /** QA-588 자체상품코드(자체옵션코드 포함) — 자체상품코드 기준 집계 때. */
  selfProductCode?: string | null;
  productName: string;
  /** 결제확정(CAPTURED) 매출액 KRW(정수 원). */
  salesAmountKrw: number;
  quantity: number;
  orderCount: number;
}

export interface SalesAggregationPage {
  rows: ReadonlyArray<SalesAggregationRow>;
  meta: ApiMeta & { dateBasis?: SalesAggregationDateBasis | null };
}

const BASE = '/api/admin/v1/orders/sales-aggregation';

export const buildSalesAggregationQuery = (p: SalesAggregationParams): string => {
  const qs = new URLSearchParams();
  if (p.from) qs.set('from', p.from);
  if (p.to) qs.set('to', p.to);
  if (p.productCode && p.productCode.trim()) qs.set('productCode', p.productCode.trim());
  const keyword = p.keyword?.trim();
  if (keyword) {
    qs.set('searchType', p.searchType ?? 'PRODUCT_CODE');
    qs.set('keyword', keyword);
    // 하위 호환 — 검색어 종류를 모르는 BE 도 상품코드 검색은 지금처럼(productCode) 동작하게.
    if ((p.searchType ?? 'PRODUCT_CODE') === 'PRODUCT_CODE' && !qs.has('productCode')) qs.set('productCode', keyword);
  }
  if (p.groupBy && p.groupBy !== 'PRODUCT_CODE') qs.set('groupBy', p.groupBy);
  if (p.dateBasis && p.dateBasis !== 'ORDERED_AT') qs.set('dateBasis', p.dateBasis);
  if (p.sort) qs.set('sort', p.sort);
  // 서버는 0-based page.
  qs.set('page', String(p.page != null ? Math.max(0, p.page - 1) : 0));
  if (p.size) qs.set('size', String(p.size));
  return qs.toString();
};

export const salesAggregationQueryKey = (p: SalesAggregationParams) =>
  ['admin', 'sales-aggregation', p] as const;

interface SalesEnvelope {
  success?: boolean;
  data?: SalesAggregationRow[];
  meta?: ApiMeta;
  error?: { code?: string; message?: string } | null;
}

export const useSalesAggregationQuery = (p: SalesAggregationParams) =>
  useQuery<SalesAggregationPage>({
    queryKey: salesAggregationQueryKey(p),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}?${buildSalesAggregationQuery(p)}`);
      if (!res.ok) {
        throw new Error(`sales-aggregation failed: ${res.status}`);
      }
      const env = (await res.json()) as SalesEnvelope;
      if (env.success === false) {
        throw new Error(env.error?.message ?? env.error?.code ?? 'sales-aggregation error');
      }
      return {
        rows: env.data ?? [],
        meta: env.meta ?? { traceId: '', timestamp: '' },
      };
    },
  });
